import { useCallback, useSyncExternalStore } from 'react';

/**
 * Tailwind's `lg` breakpoint as a media query.
 *
 * Expressed in `rem`, not `px`, to match what Tailwind v4 actually emits (`min-width: 64rem`).
 * A `px` query would disagree with the CSS for anyone whose browser root font size isn't 16px —
 * the JS and the stylesheet would flip at different widths.
 *
 * Keep in sync with the `lg:` variants used alongside it.
 */
export const BREAKPOINT_LG = '(min-width: 64rem)';

/**
 * Subscribes to a media query and returns whether it currently matches.
 *
 * Built on `useSyncExternalStore` rather than `useState` + `useEffect` so the snapshot is read
 * during render: the first paint is already correct, with no flash of the wrong layout and no
 * throwaway second render on mount.
 *
 * Use this to render ONE of two layouts rather than rendering both and hiding one with CSS. That
 * keeps the DOM half the size and — more importantly — means a portalled overlay (a Radix dropdown
 * or tooltip) is unmounted along with its trigger when the breakpoint flips, instead of being left
 * floating with nothing to anchor to.
 *
 * In Jest, `jest.setup.cjs` stubs `matchMedia` to evaluate min-/max-width against
 * `window.innerWidth`. jsdom's default viewport is 1024px, which satisfies `BREAKPOINT_LG`, so a
 * component renders its WIDE shape unless a test says otherwise. To exercise the small-screen
 * shape, narrow the viewport before rendering:
 *
 *   window.innerWidth = 375;
 *
 * @param {string} query A CSS media query, e.g. `BREAKPOINT_LG`.
 * @returns {boolean} `true` while the query matches. Falls back to `false` where `matchMedia` is
 *   unavailable, so callers degrade to their small-screen layout rather than crashing.
 */
export function useMediaQuery(query) {
  const subscribe = useCallback(
    (onStoreChange) => {
      if (typeof globalThis.matchMedia !== 'function') return () => {};
      const mql = globalThis.matchMedia(query);

      // Safari < 14 and some embedded webviews only implement the deprecated addListener/
      // removeListener pair, so fall back rather than silently never updating.
      // NOSONAR javascript:S1874 — intentional legacy fallback, see the note above.
      if (mql.addEventListener) {
        mql.addEventListener('change', onStoreChange);
        return () => mql.removeEventListener('change', onStoreChange);
      }
      mql.addListener(onStoreChange); // NOSONAR javascript:S1874
      return () => mql.removeListener(onStoreChange); // NOSONAR javascript:S1874
    },
    [query]
  );

  const getSnapshot = useCallback(() => {
    if (typeof globalThis.matchMedia !== 'function') return false;
    return globalThis.matchMedia(query).matches;
  }, [query]);

  // No viewport to measure outside the browser. This app renders client-side only, so this is
  // hygiene rather than something that runs today.
  const getServerSnapshot = () => false;

  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}
